import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useBillingStore } from '@/stores/billingStore';
import { useAuthStore } from '@/stores/authStore';
import i18n from '@/i18n';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // sends HttpOnly refresh token cookie
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Restores the session from the HttpOnly refresh cookie on app boot.
 * Uses plain axios so the apiClient 401 interceptor cannot recurse.
 * The in-flight promise is shared: refresh tokens are single-use, so two
 * concurrent calls (React StrictMode mounts effects twice) would race the
 * rotation and desynchronise the cookie from the stored hash.
 */
let restoreSessionPromise: Promise<boolean> | null = null;

export const tryRestoreSession = (): Promise<boolean> => {
  restoreSessionPromise ??= (async () => {
    try {
      const { data } = await axios.post<{ accessToken: string; role: string; tenantId: string | null }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      useAuthStore.getState().setAuth(data.accessToken, data.role, data.tenantId);
      return true;
    } catch {
      return false;
    }
  })();
  return restoreSessionPromise;
};

// Attach access token to every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Tell the API which language to localise responses in (e.g. validation messages),
  // following the app's chosen language rather than the browser's Accept-Language.
  config.headers['X-Locale'] = i18n.language || 'ro';
  return config;
});

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (err: unknown, token: string | null) => {
  pendingQueue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve(token!)));
  pendingQueue = [];
};

// Auto-refresh on 401
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 402 — the subscription has lapsed and the account is read-only. Surfaced once, globally,
    // because it can come back from any write on any screen and a silent failure would read as a
    // bug rather than an unpaid bill. Reads are unaffected, so the app stays usable behind it.
    if (error.response?.status === 402) {
      const detail = (error.response.data as { title?: string } | undefined)?.title;
      useBillingStore.getState().setLocked(
        // The server's own title is already localised (messages.ro.json); this is only the
        // fallback when it sends none. i18n.t rather than a hook — there is no component here.
        detail ?? i18n.t('billing.locked', 'This account is read-only because the subscription is unpaid.'),
      );
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // A 401 from an auth call is an ANSWER, not an expired session. Signing in with the wrong
    // password legitimately returns 401, and treating that as "the session died" sent it through the
    // refresh-then-`window.location.href` path below — a full page reload that destroyed the error
    // LoginPage had just set, leaving the user with a silently blanked form and no idea why.
    //
    // Worse than confusing: with no message people simply retry, five failures lock the account for
    // fifteen minutes, and the lockout notice was swallowed by the same reload. See #459.
    if (/\/auth\/(login|register|refresh|demo-login)/.test(original.url ?? '')) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<{ accessToken: string; role: string; tenantId: string }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      useAuthStore.getState().setAuth(data.accessToken, data.role, data.tenantId);
      processQueue(null, data.accessToken);
      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return apiClient(original);
    } catch (err) {
      processQueue(err, null);
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);
